import { types, flow } from 'mobx-state-tree';
import axios from 'axios';

// Ordered pipeline stages
export const STAGES = [
  'approval_pending',
  'scheduled',
  'recording',
  'queued',
  'transcribing',
  'transcribed',
  'identified',
  'needs_triage',
  'assigned',
  'analyzing',
  'delivered',
  'email_sent',
  'failed',
];

export const STAGE_CONFIG = {
  approval_pending: { label: 'Approval',     icon: '⏳', attention: true  },
  scheduled:        { label: 'Scheduled',    icon: '📅'                   },
  recording:        { label: 'Recording',    icon: '🎬'                   },
  queued:           { label: 'Queued',       icon: '⏱'                    },
  transcribing:     { label: 'Transcribing', icon: '📝'                   },
  transcribed:      { label: 'Transcribed',  icon: '📄'                   },
  identified:       { label: 'Identified',   icon: '👥'                   },
  needs_triage:     { label: 'Triage',       icon: '⚠️', attention: true  },
  assigned:         { label: 'Assigned',     icon: '👤'                   },
  analyzing:        { label: 'Analyzing',    icon: '💡'                   },
  delivered:        { label: 'Delivered',    icon: '✓',  terminal: true   },
  email_sent:       { label: 'Email sent',   icon: '✓',  terminal: true   },
  failed:           { label: 'Failed',       icon: '✗',  terminal: true, attention: true },
};

// Stages where an item is considered "stuck" if it hasn't progressed after this threshold
const STUCK_STAGES = new Set(['recording', 'queued', 'transcribing', 'transcribed', 'identified', 'assigned', 'analyzing']);
const STUCK_THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2 hours

function isStuck(item) {
  if (!STUCK_STAGES.has(item.stage)) return false;
  const ts = item.createdAt || item.scheduledStartTime;
  if (!ts) return false;
  return (Date.now() - new Date(ts)) > STUCK_THRESHOLD_MS;
}

// Maps meeting status values from the API to our pipeline stage names.
// Keep in lockstep with BOT_STATUS in
// backend/murphy/constants/status-constants.js — anything not listed here
// falls through to the 'scheduled' default at the call site, so a missing
// key surfaces as "Awaiting meeting start" even when the bot is actively
// recording or analyzing.
const STATUS_TO_STAGE = {
  scheduled:               'scheduled',
  joining_call:            'recording',
  in_call_recording:       'recording',
  in_call_not_recording:   'recording',
  in_progress:             'recording',
  recording:               'recording',
  partial_recording:       'recording',
  done:                    'queued',
  downloading:             'queued',
  queued:                  'queued',
  transcribing:            'transcribing',
  transcription_complete:  'transcribed',
  transcription_completed: 'transcribed',
  identifying:             'transcribed',
  identified:              'identified',
  needs_triage:            'needs_triage',
  assigned:                'assigned',
  analyzing:               'analyzing',
  delivered:               'delivered',
  completed:               'delivered',
  // Retriable failures — surface as 'failed', retriable flag set by DLQ
  analysis_failed:         'failed',
  assignment_failed:       'failed',
  download_failed:         'failed',
  transcription_failed:    'failed',
  identification_failed:   'failed',
  fatal:                   'failed',
  failed:                  'failed',
  retrying:                'queued',
};

export const TRIAGE_REASON_LABELS = {
  multiple_companies:                   'User belongs to multiple companies',
  multiple_users:                       'Multiple Multiply users in meeting',
  multiple_multiply_users_identified:   'Multiple Multiply users in meeting',
  no_multiply_user:                     'No Multiply user found',
  no_multiply_users_identified:         'No Multiply users found',
  unmatched_participants:               'Some participants could not be matched',
  non_salesperson_user:                 'Non-salesperson user in meeting',
};

function computeUrgency(item) {
  const now = Date.now();

  if (item.stage === 'approval_pending') {
    const deadline = item.approvalData?.approvalDeadline;
    if (deadline) {
      const minsLeft = (new Date(deadline) - now) / 60000;
      if (minsLeft < 0)   return 100;
      if (minsLeft < 30)  return 90;
      if (minsLeft < 120) return 80;
    }
    return 75;
  }

  if (item.stage === 'needs_triage') {
    const ageH = item.createdAt ? (now - new Date(item.createdAt)) / 3600000 : 0;
    const hi = item.triageData?.priority === 'high';
    if (ageH > 24 && hi) return 72;
    if (ageH > 24)       return 68;
    if (hi)              return 65;
    return 60;
  }

  if (item.stage === 'failed')     return 55;
  if (item.stage === 'recording')  return 40;
  if (item.stage === 'analyzing')  return 30;
  if (item.stage === 'scheduled')  return 20;
  if (item.stage === 'delivered')  return 5;
  if (item.stage === 'email_sent') return 5;
  return 15; // queued, transcribing, transcribed, identified, assigned
}

function computeIssueText(item) {
  if (item.stage === 'approval_pending') {
    const deadline = item.approvalData?.approvalDeadline;
    if (deadline) {
      const minsLeft = Math.floor((new Date(deadline) - Date.now()) / 60000);
      if (minsLeft < 0)  return 'Deadline passed';
      if (minsLeft < 60) return `Awaiting approval — ${minsLeft}m until deadline`;
      return `Awaiting approval — ${Math.floor(minsLeft / 60)}h until deadline`;
    }
    return 'Awaiting approval';
  }
  if (item.stage === 'needs_triage') {
    return TRIAGE_REASON_LABELS[item.triageData?.triageReason] || 'Needs manual assignment';
  }
  if (item.stage === 'failed') {
    const err = item.dlqData?.errorMessage || item.dlqData?.error || item.meetingData?.errorMessage;
    return err ? String(err).substring(0, 80) : 'Processing failed';
  }
  if (item.stage === 'assigned')     return `Assigned to ${item.meetingData?.assignedTo || 'user'}`;
  if (item.stage === 'delivered')    return 'Pipeline complete';
  if (item.stage === 'email_sent')   return 'Pipeline complete';
  if (item.stage === 'analyzing')    return 'AI analysis in progress';
  if (item.stage === 'recording')    return 'Bot recording in progress';
  if (item.stage === 'scheduled')    return 'Awaiting meeting start';
  if (item.stage === 'transcribing') return 'Transcription in progress';
  if (item.stage === 'transcribed')  return 'Participants being identified';
  if (item.stage === 'identified')   return 'Routing for assignment';
  if (item.stage === 'queued')       return 'Queued for processing';
  return '';
}

// Formats a short display ID from a full UUID / bot ID
export function formatDisplayId(id) {
  if (!id) return '—';
  return id.length > 12 ? id.substring(0, 12).toUpperCase() : id.toUpperCase();
}

// Formats elapsed time from a timestamp
export function formatAge(isoString) {
  if (!isoString) return '—';
  const ms = Date.now() - new Date(isoString);
  const mins = Math.floor(ms / 60000);
  if (mins < 1)    return 'Just now';
  if (mins < 60)   return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)    return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function normalizeItems(approvals, meetings, triageItems, dlq) {
  const items = [];

  // Index triage records by botId for fast lookup
  const triageByBotId = new Map();
  for (const t of triageItems) {
    if (t.botId) triageByBotId.set(t.botId, t);
  }

  // Collect all DLQ items with their category tag
  const allDlqItems = [
    ...(dlq.retriable || []).map(i => ({ ...i, _dlqCategory: 'retriable' })),
    ...(dlq.permanent || []).map(i => ({ ...i, _dlqCategory: 'permanent' })),
  ];
  const dlqBotIds = new Set(allDlqItems.map(d => d.id).filter(Boolean));

  // --- 1. Pending approval requests ---
  for (const req of approvals) {
    if (req.status !== 'pending') continue;
    const item = {
      id: `approval_${req.requestId}`,
      sourceType: 'approval',
      stage: 'approval_pending',
      meetingTitle: req.meetingTitle,
      meetingUrl: req.meetingUrl,
      scheduledStartTime: req.scheduledStartTime,
      createdAt: req.createdAt,
      participantEmails: (req.participants || []).map(p => p.email || p),
      approvalData: req,
      meetingData: null,
      triageData: null,
      dlqData: null,
    };
    item.urgencyScore = computeUrgency(item);
    item.issueText = computeIssueText(item);
    items.push(item);
  }

  // --- 2. DLQ (failed) items — takes precedence over meeting records ---
  for (const d of allDlqItems) {
    if (!d.id) continue;
    const item = {
      id: `dlq_${d.id}`,
      sourceType: 'dlq',
      stage: 'failed',
      meetingTitle: d.meetingTitle,
      meetingUrl: d.meetingUrl,
      scheduledStartTime: d.scheduledStartTime,
      createdAt: d.createdAt,
      participantEmails: [],
      approvalData: null,
      meetingData: null,
      triageData: null,
      dlqData: d,
    };
    item.urgencyScore = computeUrgency(item);
    item.issueText = computeIssueText(item);
    items.push(item);
  }

  // --- 3. Active pipeline meetings (skip if already in DLQ) ---
  const meetingBotIds = new Set();
  for (const m of meetings) {
    const botId = m.id || m.botId;
    if (!botId || dlqBotIds.has(botId)) continue;

    meetingBotIds.add(botId);

    let rawStage = STATUS_TO_STAGE[m.status] || 'scheduled';
    // If the analyzer has fired the recap email, advance past 'delivered' to
    // the terminal 'email_sent' UI stage. Status stays 'delivered' server-side.
    if (rawStage === 'delivered' && m.emailSentAt) {
      rawStage = 'email_sent';
    }
    const triageData = triageByBotId.get(botId) || null;
    // If we have a triage record for this meeting, show it as needs_triage
    const stage = triageData ? 'needs_triage' : rawStage;

    const item = {
      id: `meeting_${botId}`,
      sourceType: 'meeting',
      stage,
      meetingTitle: m.meetingTitle,
      meetingUrl: m.meetingUrl,
      scheduledStartTime: m.scheduledStartTime,
      createdAt: m.createdAt,
      participantEmails: m.validatedParticipants || [],
      approvalData: null,
      meetingData: m,
      triageData,
      dlqData: null,
    };
    item.urgencyScore = computeUrgency(item);
    item.issueText = computeIssueText(item);
    items.push(item);
  }

  // --- 4. Orphan triage records (no matching meeting record in pipeline) ---
  for (const t of triageItems) {
    if (!t.botId || meetingBotIds.has(t.botId) || dlqBotIds.has(t.botId)) continue;
    const item = {
      id: `triage_${t.triageId}`,
      sourceType: 'meeting',
      stage: 'needs_triage',
      meetingTitle: t.bot?.meetingTitle || null,
      meetingUrl: t.bot?.meetingUrl || null,
      scheduledStartTime: t.bot?.scheduledStartTime || null,
      createdAt: t.createdAt,
      participantEmails: t.participantEmails || [],
      approvalData: null,
      meetingData: t.bot || null,
      triageData: t,
      dlqData: null,
    };
    item.urgencyScore = computeUrgency(item);
    item.issueText = computeIssueText(item);
    items.push(item);
  }

  items.sort((a, b) => b.urgencyScore - a.urgencyScore);
  return items;
}

const LiveMeetingsStore = types
  .model('LiveMeetingsStore', {
    items: types.optional(types.frozen(), []),
    loading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
    lastRefreshed: types.maybeNull(types.string),
  })
  .actions(self => ({

    loadAll: flow(function* (silent = false) {
      if (!silent) self.loading = true;
      self.error = null;

      try {
        const [approvalsRes, meetingsRes, triageRes, dlqRes] = yield Promise.all([
          axios.get('/murphy/approval?includeExpired=false'),
          axios.get('/murphy/meetings?limit=1000'),
          axios.get('/murphy/triage?status=pending'),
          axios.get('/murphy/dlq'),
        ]);

        const approvals = approvalsRes.data.requests || [];
        const meetings = meetingsRes.data.meetings || [];
        const triage = Array.isArray(triageRes.data) ? triageRes.data : [];
        const dlq = dlqRes.data || {};

        self.items = normalizeItems(approvals, meetings, triage, dlq);
        self.lastRefreshed = new Date().toISOString();
      } catch (err) {
        console.error('LiveMeetingsStore.loadAll failed:', err);
        self.error = err.message;
      } finally {
        self.loading = false;
      }
    }),

    approveRequest: flow(function* (requestId, currentUserName) {
      try {
        yield axios.patch(`/murphy/approval/${requestId}`, {
          action: 'approve',
          userId: currentUserName,
        });
        yield self.loadAll(true);
      } catch (err) {
        console.error('approveRequest failed:', err);
        throw err;
      }
    }),

    denyRequest: flow(function* (requestId, currentUserName) {
      try {
        yield axios.patch(`/murphy/approval/${requestId}`, {
          action: 'deny',
          userId: currentUserName,
        });
        yield self.loadAll(true);
      } catch (err) {
        console.error('denyRequest failed:', err);
        throw err;
      }
    }),

    assignTriage: flow(function* (triageId, payload) {
      try {
        yield axios.patch(`/murphy/triage/${triageId}`, {
          status: 'assigned',
          assignedCompanyId: payload.companyId,
          assignedTo: payload.assignedTo,
          notes: payload.notes || null,
        });
        yield self.loadAll(true);
      } catch (err) {
        console.error('assignTriage failed:', err);
        throw err;
      }
    }),

    markTriageReviewed: flow(function* (triageId, notes) {
      try {
        yield axios.patch(`/murphy/triage/${triageId}`, {
          status: 'reviewed',
          notes: notes || null,
        });
        yield self.loadAll(true);
      } catch (err) {
        console.error('markTriageReviewed failed:', err);
        throw err;
      }
    }),

    retryFailed: flow(function* (botId) {
      try {
        yield axios.post(`/murphy/dlq/${botId}/retry`);
        yield self.loadAll(true);
      } catch (err) {
        console.error('retryFailed failed:', err);
        throw err;
      }
    }),

    escalate: flow(function* (itemId) {
      // Backend endpoint TBD — placeholder
      try {
        yield axios.post('/murphy/escalate', { itemId });
        yield self.loadAll(true);
      } catch (err) {
        console.error('escalate failed:', err);
        throw err;
      }
    }),

  }))
  .views(self => ({

    get stageCounts() {
      const counts = { all: self.items.length, stuck: 0 };
      for (const s of STAGES) counts[s] = 0;
      for (const item of self.items) {
        if (counts[item.stage] !== undefined) counts[item.stage]++;
        if (isStuck(item)) counts.stuck++;
      }
      return counts;
    },

    filteredItems(stages) {
      if (!stages || stages.length === 0) return self.items;
      if (stages.includes('stuck')) {
        const otherStages = stages.filter(s => s !== 'stuck');
        return self.items.filter(item =>
          isStuck(item) || otherStages.includes(item.stage)
        );
      }
      return self.items.filter(item => stages.includes(item.stage));
    },

  }));

export default LiveMeetingsStore;
